/**
 * Shared CSV transport primitives for G1's browser-built exports.
 *
 * Byte-identical mirror of the relay's relay/src/lib/csvTransport.ts. The relay
 * and frontend are separate build targets and cannot import across the boundary,
 * so this implementation and the relay's are pinned in lockstep by the shared
 * fixture (csvTransport.fixture.ts). Change the two copies together.
 *
 * The two-function cell split is load-bearing and must not be collapsed:
 *   - csvCell     — RAW. Escaping only, value never mutated. Use for identifiers,
 *                   numbers (including negatives like "-5.00"), and timestamps.
 *   - csvTextCell — TEXT. Formula-neutralize, then escape. Use ONLY for untrusted
 *                   human text; never for the raw columns above.
 */

/** UTF-8 byte-order mark. Emitted exactly once, at the head of the file. */
export const CSV_BOM = '\uFEFF';

/**
 * RFC 4180 field escaping: wrap in double quotes when the value contains a comma,
 * a double quote, CR, or LF; double any embedded quote. `null`/`undefined` → "".
 */
export function escapeCsvField(value: unknown): string {
  if (value == null) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Spreadsheet formula-injection guard. Prefixes a `'` when the text begins with
 * optional horizontal whitespace followed by `=`, `+`, `-`, or `@`. Applies to
 * plain-text cells only (see csvTextCell).
 */
export function neutralizeFormula(text: string): string {
  return /^[\t\r ]*[=+\-@]/.test(text) ? `'${text}` : text;
}

/** RAW cell — escaping only. See module header for when to use. */
export function csvCell(value: unknown): string {
  return escapeCsvField(value);
}

/** TEXT cell — formula-neutralize untrusted human text, then escape. */
export function csvTextCell(value: unknown): string {
  if (value == null) return '';
  return escapeCsvField(neutralizeFormula(String(value)));
}

/**
 * Assemble the final delivered file. `headers` are raw column names (escaped
 * here). `rows` are cell arrays already escaped via csvCell/csvTextCell. Produces
 * one leading BOM and CRLF record endings, no trailing newline.
 */
export function buildCsv(
  headers: readonly string[],
  rows: readonly (readonly string[])[],
): string {
  const lines = new Array<string>(rows.length + 1);
  lines[0] = headers.map(escapeCsvField).join(',');
  for (let i = 0; i < rows.length; i += 1) {
    lines[i + 1] = rows[i].join(',');
  }
  return CSV_BOM + lines.join('\r\n');
}
